import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { Kpi, Skeleton, fmt } from "@/pages/admin/AdminPages";
import api from "@/lib/api";

const sel = "rounded-lg border border-hborder bg-white px-3 py-2 text-sm outline-none focus:border-tblue";
const TABS = [["api", "API"], ["vitals", "Web Vitals"], ["funnel", "Funnel"], ["deps", "Dependencies"]];

const THRESH = { LCP: [2500, 4000, "ms"], INP: [200, 500, "ms"], CLS: [0.1, 0.25, ""], FCP: [1800, 3000, "ms"], TTFB: [800, 1800, "ms"] };
const tone = (name, v) => (v <= THRESH[name][0] ? "text-emerald-600" : v <= THRESH[name][1] ? "text-[#B47500]" : "text-coral");
const vfmt = (name, v) => (name === "CLS" ? v.toFixed(3) : `${Math.round(v)} ms`);
const ms = (v) => (v == null ? "—" : `${Math.round(v)} ms`);

function ApiTab() {
  const [hours, setHours] = useState(24);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/admin/performance/api", { params: { hours } }).then((r) => setD(r.data)).catch(() => setD(false)); }, [hours]);
  if (d === null) return <Skeleton />;
  if (d === false) return <p className="text-sm text-coral">Could not load API metrics.</p>;
  const maxP = Math.max(1, ...d.timeline.map((t) => t.p95 || 0));
  return (
    <div className="space-y-6" data-testid="perf-api">
      <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className={sel} data-testid="perf-api-hours">
        <option value={1}>Last hour</option><option value={24}>Last 24 hours</option><option value={168}>Last 7 days</option><option value={720}>Last 30 days</option>
      </select>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi testId="api-kpi-count" label="Requests" value={d.count} />
        <Kpi testId="api-kpi-p50" label="Latency p50" value={ms(d.p50)} />
        <Kpi testId="api-kpi-p95" label="Latency p95" value={ms(d.p95)} />
        <Kpi testId="api-kpi-errors" label="Error rate (5xx)" value={<span className={d.error_rate > 1 ? "text-coral" : ""}>{d.error_rate}%</span>} sub={`${d.errors} failed requests`} />
      </div>
      {d.timeline.length > 0 && (
        <section className="rounded-md border border-hborder bg-white p-5" data-testid="api-timeline">
          <h2 className="font-serif text-lg text-heading">p95 latency by hour</h2>
          <div className="mt-4 flex h-28 items-end gap-1">
            {d.timeline.map((t) => <div key={t.hour} title={`${t.hour} · p95 ${ms(t.p95)} · ${t.count} req`} className="flex-1 rounded-t bg-tblue/70" style={{ height: `${Math.max(4, ((t.p95 || 0) / maxP) * 100)}%` }} />)}
          </div>
        </section>
      )}
      <section className="overflow-x-auto rounded-md border border-hborder bg-white hv-scroll-thin">
        <table className="w-full min-w-[640px] text-left text-sm" data-testid="api-endpoints-table">
          <thead className="border-b border-hborder bg-softcream text-xs uppercase tracking-wider text-muted"><tr><th className="px-4 py-3">Endpoint</th><th className="px-4 py-3">Calls</th><th className="px-4 py-3">p50</th><th className="px-4 py-3">p95</th><th className="px-4 py-3">Error %</th></tr></thead>
          <tbody>{d.endpoints.map((e) => (
            <tr key={e.endpoint} className="border-b border-hborder/60 last:border-0"><td className="px-4 py-2.5 font-mono text-xs text-heading">{e.endpoint}</td><td className="px-4 py-2.5">{e.count}</td><td className="px-4 py-2.5">{ms(e.p50)}</td><td className={`px-4 py-2.5 ${e.p95 > 1000 ? "font-medium text-coral" : ""}`}>{ms(e.p95)}</td><td className={`px-4 py-2.5 ${e.error_pct ? "text-coral" : "text-muted"}`}>{e.error_pct}</td></tr>
          ))}</tbody>
        </table>
        {d.endpoints.length === 0 && <p className="p-8 text-center text-sm text-muted">No API traffic recorded in this window yet.</p>}
      </section>
      {d.slowest.length > 0 && (
        <section className="rounded-md border border-hborder bg-white p-5" data-testid="api-slowest">
          <h2 className="font-serif text-lg text-heading">Slowest requests</h2>
          <ul className="mt-2 divide-y divide-hborder/70 text-sm">{d.slowest.map((s, i) => <li key={i} className="flex justify-between gap-3 py-2"><span className="truncate font-mono text-xs">{s.endpoint} <span className="text-muted">· {s.status}</span></span><span className="shrink-0 text-xs text-muted">{ms(s.duration_ms)} · {fmt(s.ts)}</span></li>)}</ul>
        </section>
      )}
    </div>
  );
}

function DistBar({ m }) {
  const t = m.good + m.needs_improvement + m.poor || 1;
  return (
    <div className="flex h-2 overflow-hidden rounded bg-softcream" title={`${m.good} good · ${m.needs_improvement} needs improvement · ${m.poor} poor`}>
      <div className="bg-emerald-500" style={{ width: `${(m.good / t) * 100}%` }} /><div className="bg-brandyellow" style={{ width: `${(m.needs_improvement / t) * 100}%` }} /><div className="bg-coral" style={{ width: `${(m.poor / t) * 100}%` }} />
    </div>
  );
}

function VitalsTab() {
  const [days, setDays] = useState(28);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/admin/performance/vitals", { params: { days } }).then((r) => setD(r.data)).catch(() => setD(false)); }, [days]);
  if (d === null) return <Skeleton />;
  if (d === false) return <p className="text-sm text-coral">Could not load Web Vitals.</p>;
  const names = Object.keys(THRESH);
  return (
    <div className="space-y-6" data-testid="perf-vitals">
      <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={sel} data-testid="perf-vitals-days"><option value={7}>Last 7 days</option><option value={28}>Last 28 days</option><option value={90}>Last 90 days</option></select>
      {d.samples === 0 ? (
        <p className="rounded-md border border-dashed border-hborder bg-white p-10 text-center text-sm text-muted" data-testid="vitals-empty">No Web Vitals collected yet. Data appears as visitors browse the public site and app (admin pages are excluded).</p>
      ) : (
        <>
          <p className="text-xs text-muted">{d.samples} measurements from {d.sessions} sessions · p75 values (Google’s “good” thresholds)</p>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="vitals-kpis">
            {names.filter((n) => d.overall[n]).map((n) => (
              <div key={n} className="rounded-md border border-hborder bg-white p-5" data-testid={`vital-${n}`}>
                <p className="text-xs font-medium uppercase tracking-wider text-muted">{n} p75</p>
                <p className={`mt-2 font-serif text-3xl ${tone(n, d.overall[n].p75)}`}>{vfmt(n, d.overall[n].p75)}</p>
                <div className="mt-3"><DistBar m={d.overall[n]} /></div>
                <p className="mt-1 text-[11px] text-muted">{d.overall[n].n} samples</p>
              </div>
            ))}
          </div>
          <section className="overflow-x-auto rounded-md border border-hborder bg-white hv-scroll-thin">
            <table className="w-full min-w-[640px] text-left text-sm" data-testid="vitals-routes-table">
              <thead className="border-b border-hborder bg-softcream text-xs uppercase tracking-wider text-muted"><tr><th className="px-4 py-3">Route</th><th className="px-4 py-3">Samples</th>{names.map((n) => <th key={n} className="px-4 py-3">{n}</th>)}</tr></thead>
              <tbody>{d.by_route.map((r) => (
                <tr key={r.route} className="border-b border-hborder/60 last:border-0"><td className="px-4 py-2.5 font-mono text-xs">{r.route}</td><td className="px-4 py-2.5">{r.samples}</td>
                  {names.map((n) => <td key={n} className={`px-4 py-2.5 ${r.metrics[n] ? tone(n, r.metrics[n].p75) : "text-muted"}`}>{r.metrics[n] ? vfmt(n, r.metrics[n].p75) : "—"}</td>)}</tr>
              ))}</tbody>
            </table>
          </section>
          <section className="rounded-md border border-hborder bg-white p-5" data-testid="vitals-devices">
            <h2 className="font-serif text-lg text-heading">By device</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
              {Object.entries(d.by_device).map(([dev, m]) => (
                <div key={dev} className="rounded-lg bg-softcream p-3 text-sm"><p className="font-medium capitalize text-heading">{dev}</p>
                  {names.filter((n) => m[n]).map((n) => <p key={n} className="flex justify-between text-xs text-muted">{n}<span className={tone(n, m[n].p75)}>{vfmt(n, m[n].p75)}</span></p>)}</div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function FunnelTab() {
  const [f, setF] = useState({ days: 30, corridor: "" });
  const [d, setD] = useState(null);
  const [corridors, setCorridors] = useState([]);
  useEffect(() => { api.get("/admin/cases").then((r) => setCorridors(r.data.corridors)).catch(() => {}); }, []);
  useEffect(() => { setD(null); api.get("/admin/performance/funnel", { params: { days: f.days, ...(f.corridor ? { corridor: f.corridor } : {}) } }).then((r) => setD(r.data)).catch(() => setD(false)); }, [f]);
  if (d === null) return <Skeleton />;
  if (d === false) return <p className="text-sm text-coral">Could not load funnel.</p>;
  const max = Math.max(1, ...d.steps.map((s) => s.count));
  return (
    <div className="space-y-6" data-testid="perf-funnel">
      <div className="flex gap-3">
        <select value={f.days} onChange={(e) => setF({ ...f, days: Number(e.target.value) })} className={sel} data-testid="funnel-days"><option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option><option value={365}>12 months</option></select>
        <select value={f.corridor} onChange={(e) => setF({ ...f, corridor: e.target.value })} className={sel} data-testid="funnel-corridor"><option value="">All corridors</option>{corridors.map((c) => <option key={c}>{c}</option>)}</select>
      </div>
      <section className="rounded-md border border-hborder bg-white p-5">
        <div className="space-y-3">
          {d.steps.map((s) => (
            <div key={s.key} data-testid={`funnel-step-${s.key}`}>
              <div className="flex justify-between text-sm"><span className="text-heading">{s.label}</span><span className="text-muted">{s.count}{s.pct_of_prev != null && s.key !== "register" ? ` · ${s.pct_of_prev}% of prev` : ""}</span></div>
              <div className="mt-1 h-3 rounded bg-softcream"><div className="h-3 rounded bg-tblue transition-all" style={{ width: `${(s.count / max) * 100}%` }} /></div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted">Visits by device: {Object.keys(d.visits_by_device).length ? Object.entries(d.visits_by_device).map(([k, v]) => `${k} ${v}`).join(" · ") : "no sessions recorded yet"}. Stages are independent counts for the period, not a strict cohort.</p>
      </section>
      {d.by_route.length > 0 && (
        <section className="overflow-x-auto rounded-md border border-hborder bg-white hv-scroll-thin">
          <table className="w-full text-left text-sm" data-testid="funnel-corridor-table">
            <thead className="border-b border-hborder bg-softcream text-xs uppercase tracking-wider text-muted"><tr><th className="px-4 py-3">Corridor / category</th><th className="px-4 py-3">Cases</th><th className="px-4 py-3">Ready</th><th className="px-4 py-3">Approved by review</th></tr></thead>
            <tbody>{d.by_route.map((r) => <tr key={r.route} className="border-b border-hborder/60 last:border-0"><td className="px-4 py-2.5">{r.route}</td><td className="px-4 py-2.5">{r.cases}</td><td className="px-4 py-2.5">{r.ready}</td><td className="px-4 py-2.5">{r.approved}</td></tr>)}</tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function DepsTab() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { setD(null); api.get("/admin/health/dependencies").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="perf-deps">
      <button onClick={load} data-testid="deps-refresh" className="flex items-center gap-1.5 rounded-lg border border-hborder bg-white px-3 py-2 text-sm"><RefreshCw size={14} /> Re-check now</button>
      {d === null ? <Skeleton /> : d === false ? <p className="text-sm text-coral">Could not run health checks.</p> : (
        <div className="grid gap-4 md:grid-cols-2">
          {d.dependencies.map((x) => (
            <div key={x.name} className="rounded-md border border-hborder bg-white p-5" data-testid={`dep-${x.name.split(" ")[0].toLowerCase()}`}>
              <div className="flex items-center justify-between"><h3 className="font-serif text-lg text-heading">{x.name}</h3>
                <span className={`flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs ${x.ok ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-red-700"}`}><span className={`h-2 w-2 rounded ${x.ok ? "bg-emerald-500" : "bg-red-500"}`} />{x.ok ? "Operational" : "Attention"}</span></div>
              <p className="mt-2 text-sm text-muted">{x.detail}</p>
              <p className="mt-2 text-xs text-muted">{x.latency_ms != null ? `Latency ${x.latency_ms} ms · ` : ""}Uptime {x.uptime_pct}% over last {x.checks_sampled} checks</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPerformance() {
  const [tab, setTab] = useState("api");
  return (
    <AdminShell title="Performance & analytics" subtitle="First-party API metrics, Web Vitals, funnel and dependency health">
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-hborder" role="tablist">
        {TABS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`perf-tab-${k}`}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === k ? "border-tblue text-tblue" : "border-transparent text-muted hover:text-heading"}`}>{l}</button>
        ))}
      </div>
      {tab === "api" && <ApiTab />}
      {tab === "vitals" && <VitalsTab />}
      {tab === "funnel" && <FunnelTab />}
      {tab === "deps" && <DepsTab />}
    </AdminShell>
  );
}
